import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Building2,
  Store,
  LandPlot,
  Star,
} from "lucide-react";
import Navbar from "../../../components/Navbar/Navbar";
import Hero from "../../../components/Hero/Hero";
import PropertyCard from "../../../components/PropertyCard/PropertyCard";
import Footer from "../../../components/Footer/Footer";
import Loader from "../../../components/Loader/Loader";
import { getProperties, getCities } from "../../../services/api";
import "./Home.css";

export default function Home() {
  const [featured, setFeatured] = useState(null);
  const [residential, setResidential] = useState(null);
  const [commercial, setCommercial] = useState(null);
  const [plots, setPlots] = useState(null);
  const [cities, setCities] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        // ── Properties ──
        const all = await getProperties();

        // Only show published + active properties
        const published = all.filter(
          (p) =>
            (p.status === "Published" || p.status === "published") &&
            p.isActive !== false
        );

        // Featured
        setFeatured(
          published
            .filter((p) => p.featured === true)
            .slice(0, 6)
        );

        // Categories (case-insensitive)
        const byCategory = (cat) =>
          published
            .filter(
              (p) =>
                p.category?.toLowerCase() === cat.toLowerCase()
            )
            .slice(0, 6);

        setResidential(byCategory("Residential"));
        setCommercial(byCategory("Commercial"));
        setPlots(byCategory("Plots") || byCategory("Plot"));

        // ── Cities ──
        const cityList = await getCities();
        setCities(cityList || []);
      } catch (err) {
        console.error("Home load failed:", err);
        // Fail gracefully
        setFeatured([]);
        setResidential([]);
        setCommercial([]);
        setPlots([]);
        setCities([]);
      }
    };

    load();
  }, []);

  return (
    <div className="home-page">
      <Navbar />
      <Hero />

      {/* ── Trust bar ── */}
      <section className="container-page trust-bar">
        <Stat icon={ShieldCheck} k="RERA" v="Verified only" />
        <Stat icon={Sparkles} k="120+" v="Premium projects" />
        <Stat
          icon={TrendingUp}
          k={cities ? String(cities.length) : "—"}
          v="Top Indian cities"
        />
        <Stat icon={ShieldCheck} k="0%" v="Brokerage" />
      </section>

      {/* ── Residential ── */}
      <CategorySection
        eyebrow="Homes"
        title="Residential Projects"
        copy="Skyline apartments, villas and gated communities."
        icon={Building2}
        items={residential}
        categorySlug="residential"
      />

      {/* ── Commercial ── */}
      <CategorySection
        eyebrow="Workspaces"
        title="Commercial Projects"
        copy="Grade-A offices, retail plazas and IT campuses."
        icon={Store}
        items={commercial}
        categorySlug="commercial"
        variant="alt"
      />

      {/* ── Plots ── */}
      <CategorySection
        eyebrow="Investments"
        title="Plotted Developments"
        copy="Gated plots with clear titles and approved layouts."
        icon={LandPlot}
        items={plots}
        categorySlug="plots"
      />

      {/* ── Featured ── */}
      <section className="featured-section">
        <div className="container-page">
          <SectionHead
            eyebrow="Handpicked"
            title="Featured Projects"
            copy="A curated shortlist from the developers we trust most."
            icon={Star}
          />

          {!featured && <Loader label="Loading featured…" />}

          {featured && featured.length > 0 && (
            <>
              <div className="featured-grid">
                {featured.map((p) => (
                  <PropertyCard key={p.id} property={p} />
                ))}
              </div>
              <div className="section-cta">
                <Link to="/projects/residential" className="btn-dark">
                  View all featured <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </>
          )}

          {featured && featured.length === 0 && (
            <p className="empty-note">No featured projects yet.</p>
          )}
        </div>
      </section>

      {/* ── Cities ── */}
      <section className="container-page section-block">
        <SectionHead
          eyebrow="Locations"
          title="Live in the city you love"
          copy="Explore projects across India's fastest-growing metros."
        />

        {!cities && <Loader label="Loading cities…" />}

        {cities && cities.length > 0 && (
          <div className="cities-grid">
            {cities.map((c) => (
              <Link
                key={c.id || c.slug || c.name}
                to={`/city/${c.slug || c.id}`}
                className="city-chip"
              >
                {c.name}
              </Link>
            ))}
          </div>
        )}

        {cities && cities.length === 0 && (
          <p className="empty-note">No cities available yet.</p>
        )}
      </section>

      {/* ── CTA ── */}
      <section className="container-page cta-section">
        <div className="cta-box">
          <div>
            <h3 className="cta-title">Ready to find your next address?</h3>
            <p className="cta-copy">
              Speak to a senior advisor — no fee, no obligation.
            </p>
          </div>
          <Link to="/contact" className="cta-btn">
            Book a Consultation <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}

/* ─── Category section ─── */
function CategorySection({
  eyebrow,
  title,
  copy,
  icon,
  items,
  categorySlug,
  variant,
}) {
  return (
    <section
      className={`section-block ${variant === "alt" ? "section-alt" : ""}`}
    >
      <div className="container-page">
        <SectionHead eyebrow={eyebrow} title={title} copy={copy} icon={icon} />

        {!items && <Loader label="Loading…" />}

        {items && items.length === 0 && (
          <p className="empty-note">No projects yet in this category.</p>
        )}

        {items && items.length > 0 && (
          <>
            <div className="featured-grid">
              {items.map((p) => (
                <PropertyCard key={p.id} property={p} />
              ))}
            </div>
            <div className="section-cta">
              <Link to={`/projects/${categorySlug}`} className="btn-outline">
                View all {title.toLowerCase()}{" "}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

/* ─── Helpers ─── */
function Stat({ icon: Icon, k, v }) {
  return (
    <div className="stat-item">
      <div className="stat-icon">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="stat-k">{k}</div>
        <div className="stat-v">{v}</div>
      </div>
    </div>
  );
}

function SectionHead({ eyebrow, title, copy, icon: Icon }) {
  return (
    <div className="section-head">
      <span className="section-eyebrow">
        {Icon && <Icon className="h-4 w-4" />}
        {eyebrow}
      </span>
      <h2 className="section-title">{title}</h2>
      {copy && <p className="section-copy">{copy}</p>}
    </div>
  );
}